import React, { useCallback, useState } from "react";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Drawer from "antd/es/drawer";
import Form from "antd/es/form";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import { SandboxConfig } from "@mapmycustomers/shared/types/Sandbox";
import { Button } from "@mapmycustomers/ui";

import useHiddenChat from "@app/util/useHiddenChat";

import SandboxItems from "./component/SandboxItems";
import styles from "./CreatePane.module.scss";

interface Props {
  config?: SandboxConfig;
  onHide: (config?: SandboxConfig) => void;
}

const EditPane: React.FC<Props> = ({ config, onHide }) => {
  const intl = useIntl();
  useHiddenChat();
  const [form] = Form.useForm<SandboxConfig>();

  const [canSave, setCanSave] = useState<boolean>(
    Object.values(config ?? {}).some((value) => (Array.isArray(value) ? value.length : value))
  );

  const handleChange = useCallback(() => {
    setCanSave(
      Object.values(form.getFieldsValue(true)).some((value) =>
        Array.isArray(value) ? value.length : value
      )
    );
  }, [form, setCanSave]);

  const handleFinish = useCallback(() => {
    const values = form.getFieldsValue(true);
    const config: Record<string, boolean | EntityType[]> = {};
    Object.keys(values).forEach(
      (key: string) =>
        (config[key] = Array.isArray(values[key]) && values[key].length === 0 ? null : values[key])
    );
    onHide(config);
  }, [form, onHide]);

  return (
    <Drawer
      className={styles.container}
      closable
      onClose={() => onHide()}
      placement="right"
      title={intl.formatMessage({
        id: "settings.sandboxEnvironment.editPane.title",
        defaultMessage: "Configure Sandbox",
        description: "Settings sandbox edit pane - title",
      })}
      visible
    >
      <Form
        className={styles.form}
        form={form}
        initialValues={config}
        onChange={handleChange}
        onFinish={handleFinish}
      >
        <div className={styles.fields}>
          <div>
            {intl.formatMessage(
              {
                id: "settings.sandboxEnvironment.editPane.description",
                defaultMessage:
                  "Select components of your current account <b>to clone into this sandbox environment</b>:",
                description: "Settings sandbox edit pane - description",
              },
              { b: (text) => <b>{text}</b> }
            )}
          </div>
          <Form.Item className={styles.integrationWarningFromItem} shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue("integrations") ? (
                <div className={styles.integrationWarning}>
                  <div>
                    <FontAwesomeIcon
                      className={styles.integrationWarningIcon}
                      icon={faInfoCircle}
                      size="lg"
                    />
                  </div>
                  <div>
                    {intl.formatMessage({
                      id: "settings.sandboxEnvironment.editPane.integrationWarning",
                      defaultMessage:
                        "Cloning Integrations: Please note that when you move into this sandbox environment for the first time, you’ll be required to re-authenticate with your CRM",
                      description: "Settings sandbox edit pane - integration Warning",
                    })}
                  </div>
                </div>
              ) : null
            }
          </Form.Item>
          <SandboxItems />
        </div>
        <div className={styles.bottom}>
          <Button disabled={!canSave} htmlType="submit" type="primary">
            {intl.formatMessage({
              id: "settings.sandboxEnvironment.editPane.action",
              defaultMessage: "Save Sandbox Configuration",
              description: "Settings sandbox edit pane - Save Sandbox Configuration",
            })}
          </Button>
        </div>
      </Form>
    </Drawer>
  );
};

export default EditPane;
